import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';
import { taskService } from '../../../services/taskService';

const TaskChat = ({ messages, familyMembers, onSendMessage, taskId }) => {
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);
  const [realtimeChannel, setRealtimeChannel] = useState(null);
  const [localMessages, setLocalMessages] = useState(messages || []);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [localMessages]);

  // Real-time subscription for new chat messages
  useEffect(() => {
    if (!taskId) return;

    const channel = taskService?.subscribeToChatMessages(
      taskId,
      (payload) => {
        if (payload?.eventType === 'INSERT') {
          setLocalMessages((prev) => [...prev, payload?.new]);
        }
      }
    );

    setRealtimeChannel(channel);

    return () => {
      if (channel) {
        taskService?.unsubscribe(channel);
      }
    };
  }, [taskId]);

  // Update local messages when messages prop changes
  useEffect(() => {
    setLocalMessages(messages || []);
  }, [messages]);

  const getMember = (senderId) => {
    return familyMembers?.find(m => m?.id === senderId);
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'h:mm a');
    } catch {
      return '';
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday?.setDate(yesterday?.getDate() - 1);

      if (date?.toDateString() === today?.toDateString()) {
        return 'Today';
      } else if (date?.toDateString() === yesterday?.toDateString()) {
        return 'Yesterday';
      } else {
        return format(date, 'MMM d, yyyy');
      }
    } catch {
      return '';
    }
  };

  const handleSend = () => {
    if (!newMessage?.trim()) return;
    onSendMessage(newMessage?.trim());
    setNewMessage('');
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  // Group messages by date
  const groupedMessages = localMessages?.reduce((groups, message) => {
    const date = formatDate(message?.timestamp);
    if (!groups?.[date]) {
      groups[date] = [];
    }
    groups?.[date]?.push(message);
    return groups;
  }, {});

  return (
    <div className="bg-muted/30">
      {/* Messages */}
      <div className="max-h-96 overflow-y-auto p-4 space-y-4">
        {localMessages?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="message-circle" size={32} className="mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          Object.entries(groupedMessages || {})?.map(([date, dateMessages]) => (
            <div key={date}>
              {/* Date separator */}
              <div className="flex items-center justify-center my-3">
                <div className="bg-card px-3 py-1 rounded-full border border-border">
                  <span className="text-xs font-medium text-muted-foreground">{date}</span>
                </div>
              </div>

              {/* Messages for this date */}
              {dateMessages?.map((message, index) => {
                const member = getMember(message?.senderId);
                const showAvatar = index === 0 || dateMessages?.[index - 1]?.senderId !== message?.senderId;

                return (
                  <div key={message?.id} className="flex items-start gap-2 mb-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {showAvatar ? (
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                          style={{ backgroundColor: member?.color || '#6B7280' }}
                        >
                          {member?.initials || '??'}
                        </div>
                      ) : (
                        <div className="w-7 h-7" />
                      )}
                    </div>

                    {/* Message content */}
                    <div className="flex-1 min-w-0">
                      {showAvatar && (
                        <div className="flex items-baseline gap-2 mb-1">
                          <span
                            className="font-semibold text-xs"
                            style={{ color: member?.color || '#6B7280' }}
                          >
                            {member?.name || 'Unknown'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatTime(message?.timestamp)}
                          </span>
                        </div>
                      )}
                      
                      <div className="bg-card border border-border rounded-lg px-3 py-2 inline-block max-w-full">
                        <p className="text-sm text-foreground whitespace-pre-wrap break-words">
                          {message?.content}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-border p-4">
        <div className="flex gap-2">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e?.target?.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message... (Press Enter to send)"
            className="flex-1 min-h-[80px] px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            rows={2}
          />
          <Button
            onClick={handleSend}
            disabled={!newMessage?.trim()}
            iconName="send"
            size="icon"
            className="self-end"
            aria-label="Send message"
          />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Tip: Use @Name to mention team members
        </p>
      </div>
    </div>
  );
};

export default TaskChat;